import { useCallback, useEffect, useState } from 'react';

import {
  addFavorite as dbAddFavorite,
  favoriteSortTimestamp,
  isActiveFavorite,
  isAppSaved,
  isSourceSaved,
  listAllFavorites,
  listFavoritesBySource,
  markSourceFavorite,
  removeFavorite as dbRemoveFavorite,
  unmarkSourceFavorite,
} from '@/db/favorites';
import type { FavoriteRow } from '@/db/schema';
import { AuthExpired, CloudflareChallenge, NetworkError, SourceError } from '@/lib/errors';
import { subscribeAccountDataRestored } from '@/features/account/accountDataEvents';
import { requestAccountDataAutoSync } from '@/features/account/accountDataAutoSync';
import { useAccountAuthReady } from '@/features/account';
import { useSourceAuthLoader } from '@/features/auth';
import { getAllSources } from '@/sources/registry';
import type { ComicCard, ComicSource, SourceAuth, SourceId } from '@/sources/types';
import { collectFavoritePages, type CollectedFavoriteCard } from './favoritesRefresh';

/**
 * Flat shape used by the library view and dedup hint pass.
 *
 * Mirrors {@link FavoriteRow} but is decoupled from the IndexedDB schema so
 * callers (sorting, dedup, rendering) don't accidentally rely on storage
 * internals (`key`, `extra`, etc.).
 */
export interface FavoriteEntry {
  sourceId: SourceId;
  comicId: string;
  addedAt: number;
  title: string;
  cover: string;
  appSaved: boolean;
  sourceSaved: boolean;
  /** Optional canonical tag strings, when the source surfaces them. */
  tags?: string[];
}

export type LibraryErrorKind = 'auth' | 'network' | 'cloudflare' | 'unknown';

export interface SourceFavoritesError {
  sourceId: SourceId;
  kind: LibraryErrorKind;
  message: string;
}

export interface UseLibraryResult {
  /** Union of all favorites across sources, source of truth = local DB. */
  entries: FavoriteEntry[];
  /** Per-source refresh errors (auth-required sources only). */
  errors: SourceFavoritesError[];
  /** Sources with local saved rows but no logged-in auth to refresh native favorites. */
  staleSources: SourceId[];
  /** True while the initial DB read or first refresh is in flight. */
  loading: boolean;
  /** Force a re-read from the local DB and a fresh refresh of auth sources. */
  refresh: () => void;
  /** Lower-cased timestamp of the last refresh, for "Last synced …" labels. */
  lastSyncedAt: number | undefined;
}

const REFRESH_TTL_MS = 60_000;

/**
 * Module-scoped, in-memory record of when each auth source was last
 * refreshed. Survives across mounts of the hook (e.g. the user navigates
 * away and back) but is cleared on full reload. We keep this OUT of
 * IndexedDB so a stale local copy never blocks a real refresh.
 */
const lastRefresh = new Map<SourceId, number>();

interface RefreshAuthSourceResult {
  error: SourceFavoritesError | null;
  attempted: boolean;
  stale: boolean;
}

function rowToEntry(row: FavoriteRow): FavoriteEntry {
  const entry: FavoriteEntry = {
    sourceId: row.source as SourceId,
    comicId: row.comicId,
    addedAt: favoriteSortTimestamp(row),
    title: row.titleSnapshot,
    cover: row.coverUrl,
    appSaved: isAppSaved(row),
    sourceSaved: isSourceSaved(row),
  };
  const tags = (row.extra as { tags?: string[] } | undefined)?.tags;
  if (tags) entry.tags = tags;
  return entry;
}

function classifyError(err: unknown): LibraryErrorKind {
  if (err instanceof AuthExpired) return 'auth';
  if (err instanceof CloudflareChallenge) return 'cloudflare';
  if (err instanceof NetworkError) return 'network';
  if (err instanceof SourceError) return 'unknown';
  return 'unknown';
}

/**
 * Mirror server-side favorites into the local DB.
 *
 * Complete source-site sync: returned cards are marked as source favorites,
 * and rows that were previously source favorites but no longer appear are
 * unmarked. Bunko app-level saves are independent and are never cleared here.
 */
async function mirrorIntoDb(
  sourceId: SourceId,
  cards: ComicCard[] | CollectedFavoriteCard[],
  existingBySource: FavoriteRow[],
): Promise<number> {
  const existingById = new Map(existingBySource.map((r) => [r.comicId, r]));
  let changed = 0;
  const syncStartedAt = Date.now();
  const seen = new Set<string>();
  for (const [idx, item] of cards.entries()) {
    const card = 'card' in item ? item.card : item;
    const sourceOrder = 'sourceOrder' in item ? item.sourceOrder : idx;
    const addedAt = syncStartedAt - sourceOrder;
    seen.add(card.id);
    const existing = existingById.get(card.id);
    if (!existing || !isSourceSaved(existing) || existing.sourceSavedAt !== addedAt) {
      await markSourceFavorite(
        { ...card, source: sourceId },
        { savedAt: addedAt, syncedAt: syncStartedAt },
      );
      changed += 1;
    }
  }
  for (const row of existingBySource) {
    if (!isSourceSaved(row) || seen.has(row.comicId)) continue;
    await unmarkSourceFavorite(sourceId, row.comicId);
    changed += 1;
  }
  return changed;
}

async function refreshAuthSource(
  source: ComicSource,
  force: boolean,
  loadAuth: (source: ComicSource) => Promise<SourceAuth | undefined>,
): Promise<RefreshAuthSourceResult> {
  if (!source.capabilities.login || !source.listFavorites) {
    return { error: null, attempted: false, stale: false };
  }
  const last = lastRefresh.get(source.id) ?? 0;
  if (!force && Date.now() - last < REFRESH_TTL_MS) {
    return { error: null, attempted: false, stale: false };
  }

  let auth: SourceAuth | undefined;
  try {
    auth = await loadAuth(source);
  } catch {
    // Decrypt failure — passphrase mismatch, mostly. Surface a single
    // "reconnect" banner instead of breaking the whole saved page.
    return {
      error: {
        sourceId: source.id,
        kind: 'auth',
        message: `Could not decrypt credentials for ${source.name}.`,
      },
      attempted: false,
      stale: true,
    };
  }
  if (!auth) return { error: null, attempted: false, stale: true };
  // Anonymous mode means the user pasted only Cloudflare bot-detection
  // cookies — they're not logged in. Calling `listFavorites` here would
  // either 401 or redirect to login and surface a misleading
  // "Reconnect" banner. Skip silently; the library's union view stays
  // backed by the local DB.
  if (auth.anonymous) return { error: null, attempted: false, stale: true };

  try {
    const cards = await collectFavoritePages(source, auth);
    const existing = await listFavoritesBySource(source.id);
    const mirroredCount = await mirrorIntoDb(source.id, cards, existing);
    if (mirroredCount > 0) {
      requestAccountDataAutoSync();
    }
    lastRefresh.set(source.id, Date.now());
    return { error: null, attempted: true, stale: false };
  } catch (err) {
    return {
      error: {
        sourceId: source.id,
        kind: classifyError(err),
        message:
          err instanceof Error
            ? err.message
            : `Failed to refresh ${source.name}: ${String(err)}`,
      },
      attempted: true,
      stale: false,
    };
  }
}

/**
 * Cross-source unified favorites view.
 *
 * Behavior:
 *  - Treats `db/favorites.ts` as the source of truth for the union view.
 *  - For sources where `capabilities.login === true && listFavorites` is
 *    implemented AND the user has local or account-synced credentials, runs a
 *    best-effort refresh and mirrors new rows into the local DB. Cached for
 *    60s in module memory.
 *  - Sources with `capabilities.login === false` (hitomi) need no refresh
 *    — the DB is their canonical store.
 *  - Per-source errors are surfaced via `errors` so the UI can render an
 *    inline "Reconnect X" banner without failing the entire page.
 */
export function useLibrary(opts: { autoRefresh?: boolean } = {}): UseLibraryResult {
  const autoRefresh = opts.autoRefresh ?? true;
  const loadAuth = useSourceAuthLoader();
  const accountAuthReady = useAccountAuthReady();
  const [entries, setEntries] = useState<FavoriteEntry[]>([]);
  const [errors, setErrors] = useState<SourceFavoritesError[]>([]);
  const [staleSources, setStaleSources] = useState<SourceId[]>([]);
  const [loading, setLoading] = useState(true);
  const [tick, setTick] = useState(0);
  const [lastSyncedAt, setLastSyncedAt] = useState<number | undefined>(undefined);

  const refresh = useCallback(() => setTick((t) => t + 1), []);

  useEffect(() => subscribeAccountDataRestored(refresh), [refresh]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      // 1. Refresh login-required sources (best-effort, parallel).
      let collectedErrors: SourceFavoritesError[] = [];
      let collectedStaleSources: SourceId[] = [];
      if (autoRefresh && accountAuthReady) {
        const sources = getAllSources().filter(
          (s) => s.capabilities.favorites && s.capabilities.login,
        );
        const results = await Promise.all(
          sources.map((s) => refreshAuthSource(s, tick > 0, loadAuth)),
        );
        collectedErrors = results
          .map((result) => result.error)
          .filter((error): error is SourceFavoritesError => error !== null);
        const localRows = await listAllFavorites();
        const localCounts = new Map<string, number>();
        for (const row of localRows) localCounts.set(row.source, (localCounts.get(row.source) ?? 0) + 1);
        collectedStaleSources = sources
          .filter((source, idx) => results[idx]?.stale && (localCounts.get(source.id) ?? 0) > 0)
          .map((source) => source.id);
        if (results.some((result) => result.attempted)) setLastSyncedAt(Date.now());
      }

      // 2. Re-read the DB regardless. The DB always wins; refresh only
      //    updates source-site flags on the auth side.
      const rows = await listAllFavorites();
      if (cancelled) return;
      setEntries(rows.filter(isActiveFavorite).map(rowToEntry));
      setErrors(collectedErrors);
      setStaleSources(collectedStaleSources);
      setLoading(false);
    })().catch((err) => {
      if (cancelled) return;
      setErrors([
        {
          sourceId: 'ehentai' as SourceId, // placeholder — unused for non-source error UI
          kind: 'unknown',
          message: err instanceof Error ? err.message : String(err),
        },
      ]);
      setStaleSources([]);
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [accountAuthReady, autoRefresh, loadAuth, tick]);

  return { entries, errors, staleSources, loading, refresh, lastSyncedAt };
}

/**
 * Convenience helper for the "save this in Bunko" affordance.
 * Source-site favorite writes are tracked separately via
 * {@link addLocalSourceFavorite}.
 */
export async function addLocalFavorite(card: ComicCard): Promise<void> {
  await dbAddFavorite(card);
}

/**
 * Remove only the Bunko app-level save. Source-site favorite state, if any,
 * remains visible in the library until removed from the source or refreshed.
 */
export async function removeLocalFavorite(sourceId: SourceId, comicId: string): Promise<void> {
  await dbRemoveFavorite(sourceId, comicId);
}

export async function addLocalSourceFavorite(
  card: ComicCard,
  opts?: { sourceNativeCat?: number },
): Promise<void> {
  await markSourceFavorite(card, opts);
}

export async function removeLocalSourceFavorite(sourceId: SourceId, comicId: string): Promise<void> {
  await unmarkSourceFavorite(sourceId, comicId);
}
